'use client'
import * as React from 'react'
import { ArrowRight, CheckCircle2, Loader2, Mail } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { trackGenerateLead } from '@/lib/analytics/ga4'
import { TurnstileWidget, useTurnstileSiteKey } from '@/lib/turnstile'

// Compact email-only newsletter signup. Posts to /api/leads with
// intent='newsletter' so it shares the existing fan-out (Slack /
// webhook / Resend / GA4 event), no new endpoint or DB schema.
//
// Used on:
//   - /blog index footer ("get the next piece in your inbox")
//   - /blog/[slug] coming-soon teaser ("notify me when this ships")
//
// `source` lets us attribute conversions to the surface that drove
// them (`/blog`, `/blog/<slug>`, etc.).

type Props = {
  source: string
  /** Headline above the input. Leave blank if you have your own copy. */
  headline?: string
  /** Sub-line below the input. */
  caption?: string
  /** Tighter padding + smaller type. */
  compact?: boolean
  /** Optional CTA text override. Defaults to "Sign me up". */
  cta?: string
}

export function NewsletterCapture({ source, headline, caption, compact, cta = 'Sign me up' }: Props) {
  const [state, setState] = React.useState<'idle' | 'submitting' | 'ok' | 'error'>('idle')
  const [message, setMessage] = React.useState('')
  const turnstileSiteKey = useTurnstileSiteKey()
  const [turnstileToken, setTurnstileToken] = React.useState('')

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setState('submitting')
    setMessage('')
    const fd = new FormData(e.currentTarget)
    const email = String(fd.get('email') || '')
    const honeypot = String(fd.get('company_url') || '')
    if (honeypot) {
      // Silent drop, same as LeadCaptureForm.
      setState('ok')
      return
    }
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          email,
          intent: 'newsletter',
          source,
          'cf-turnstile-response': turnstileToken,
        }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json?.ok) {
        setState('error')
        setMessage(json?.error || 'Something went wrong. Try again in a minute.')
        return
      }
      setState('ok')
      trackGenerateLead({ intent: 'newsletter', source })
    } catch {
      setState('error')
      setMessage('Network error. Try again in a minute.')
    }
  }

  if (state === 'ok') {
    return (
      <div className={cn('flex items-center justify-center gap-3 rounded-2xl border bg-card px-5 py-4 text-sm', compact && 'py-3 text-[13px]')}>
        <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
        <span>You&rsquo;re on the list. Next piece lands in your inbox the day it ships.</span>
      </div>
    )
  }

  return (
    <div className={cn(!compact && 'rounded-2xl border bg-card p-5 sm:p-6')}>
      {headline && (
        <p className={cn('font-display font-semibold', compact ? 'text-base' : 'text-lg md:text-xl')}>
          {headline}
        </p>
      )}
      {caption && (
        <p className={cn('mt-1 text-muted-foreground', compact ? 'text-xs' : 'text-sm')}>{caption}</p>
      )}

      <form onSubmit={onSubmit} className={cn('mt-3 flex flex-col gap-2 sm:flex-row', compact && 'mt-2')}>
        {/* Honeypot, bots fill in everything visible. */}
        <div className="absolute left-[-9999px] top-[-9999px]" aria-hidden>
          <label>
            Company website
            <input type="text" name="company_url" tabIndex={-1} autoComplete="off" />
          </label>
        </div>

        <label className="relative flex-1">
          <span className="sr-only">Email address</span>
          <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input
            type="email"
            name="email"
            required
            placeholder="you@brand.com"
            className="pl-9"
            disabled={state === 'submitting'}
          />
        </label>
        <Button
          type="submit"
          disabled={state === 'submitting' || (Boolean(turnstileSiteKey) && !turnstileToken)}
          className="shrink-0"
        >
          {state === 'submitting' ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Submitting
            </>
          ) : (
            <>
              {cta} <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>
      </form>

      {turnstileSiteKey && (
        <TurnstileWidget
          size="compact"
          onToken={setTurnstileToken}
          onExpire={() => setTurnstileToken('')}
          className="mt-3"
        />
      )}

      {state === 'error' && (
        <p className="mt-2 text-xs text-destructive">{message}</p>
      )}
      <p className={cn('mt-3 text-muted-foreground', compact ? 'text-[10px]' : 'text-[11px]')}>
        One email per piece. No drip sequences, no sales follow-up. Unsubscribe with one click.
      </p>
    </div>
  )
}
